"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Lobby = void 0;
const bitiotic_1 = require("@bitiotic/bitiotic");
const Persistent = require("./Persistent");
const ParamValidation_1 = require("./ParamValidation");
/**
 * Track players independent of any specific game.
 */
class Lobby {
    static pathForUser(user) {
        return user.uid();
    }
    static getPlayerCollection(ctx, db) {
        return db.makeCollectionRef(ctx, this.PLAYER_COLLECTION);
    }
    static getPlayerDocRef(ctx, db, player) {
        return this.getPlayerCollection(ctx, db).makeDocRef(player);
    }
    constructor(ctx, firestore, fbFunc) {
        this.players = Lobby.getPlayerCollection(ctx, firestore);
        this.createGlobalUserFunc = fbFunc.findCallable('createGlobalUser');
    }
    getPlayerDoc(_ctx, player) {
        return this.players.makeDocRef(player);
    }
    isValidUser(ctx, user) {
        const path = Lobby.pathForUser(user);
        return this.getPlayerDoc(ctx, path).get()
            .then(meta => {
            return meta.docExists();
        });
    }
    /**
     * Ensure given player exists in the global directory.
     */
    createOrUpdatePlayer(ctx, user) {
        const path = Lobby.pathForUser(user);
        return this.getPlayerDoc(ctx, path).get()
            .then(docData => {
            if (docData.docExists()) {
                ctx.log('Existing player at', path);
                const data = docData.data();
                if (!(data === null || data === void 0 ? void 0 : data.displayName)) {
                    return this.getPlayerDoc(ctx, path).patchIn(ctx, {
                        displayName: user.displayName(),
                    }).then(() => {
                        return path;
                    });
                }
                return path;
            }
            else {
                const args = {
                    userID: path,
                    displayName: user.displayName(),
                };
                ctx.log('Creating /globalPlayer for Firebase User', user.debugString());
                return this.createGlobalUserFunc(args)
                    .catch(err => {
                    throw ctx.chainError(err, `Invoking "createGlobalUser" for ${path}`);
                }).then(result => {
                    ctx.log('Created player', path, 'got', result);
                    ctx.assertEqual(result.status, 'ok');
                    return path;
                });
            }
        })
            .catch((err) => {
            throw ctx.chainError(err, 'Create/update player:', user.displayName());
        });
    }
    /*
     * Find the email address associated with the given user, to see if they have an exception
     * to the max-players limit.
     */
    getPlayerException(ctx, auth, globalCfg, userID) {
        return auth.fetchUser(userID).then(user => {
            var _a;
            const email = (_a = user === null || user === void 0 ? void 0 : user.email()) !== null && _a !== void 0 ? _a : 'no-email-address';
            return globalCfg.getPlayerExceptionEntry(ctx, email).then(maxException => {
                return {
                    maxException,
                    email: email,
                };
            });
        });
    }
    // Before creating a new player (racily) check the current player limits.
    checkPlayerCount(ctx, auth, globalCfg, playerPath) {
        return Promise.all([
            globalCfg.getMaxGlobalPlayers(ctx),
            this.getGlobalPlayerCount(ctx),
            this.getPlayerException(ctx, auth, globalCfg, playerPath),
        ])
            .then(([maxGlobalPlayers, currGlobalPlayers, { maxException, email }]) => {
            if (currGlobalPlayers >= maxGlobalPlayers) {
                if (maxException === true) {
                    ctx.log('Too many players, but allowing exception for', email);
                    return true;
                }
                else {
                    ctx.log('Too many players, disallowing new player:', email, currGlobalPlayers, '>=', maxGlobalPlayers);
                    return false;
                }
            }
            if (maxException === false) {
                ctx.log('Banned address, disallowing new player:', email, playerPath);
                return false;
            }
            return true;
        });
    }
    // Create given player in Numbets database.  Return false if player already exists.  Admin-only.
    async serverCreatePlayer(ctx, auth, globalCfg, untrustedArgs) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrustedArgs, ['displayName', 'userID']);
        (0, ParamValidation_1.isStr)(ctx, untrustedArgs.displayName, 255);
        (0, ParamValidation_1.isID)(ctx, untrustedArgs.userID);
        (0, ParamValidation_1.isCurrentUser)(ctx, auth, untrustedArgs.userID);
        // Only players themselves can create their Numbets player
        const playerPath = Persistent.asPlayerPath(untrustedArgs.userID);
        const displayName = untrustedArgs.displayName;
        return this.checkPlayerCount(ctx, auth, globalCfg, playerPath).then((ok) => {
            if (!ok) {
                return false;
            }
            return this.getPlayerDoc(ctx, playerPath).get()
                .then(docData => {
                if (docData.docExists()) {
                    ctx.log('Player exists, ignoring:', playerPath);
                    return true;
                }
                else {
                    ctx.log('Creating player at', playerPath);
                    return this.getPlayerDoc(ctx, playerPath).set(ctx, {
                        displayName,
                    })
                        .then(() => true);
                }
            })
                .catch((err) => {
                ctx.log('Failing serverCreatePlayer:', err);
                return false;
            });
        });
    }
    setPlayerDisplayName(ctx, user, displayName) {
        const path = Lobby.pathForUser(user);
        return this.getPlayerDoc(ctx, path).patchIn(ctx, {
            displayName,
        })
            .catch((err) => {
            throw ctx.chainError(err, `Set displayname to "${displayName}" for ${user.uid()}`);
        });
    }
    // Listen for updates to given Global Player.  Perpetual.
    listenToPlayer(ctx, player, cb, errCb) {
        // XXX retries
        const unsub = bitiotic_1.CancelTracker.track(`globalPlayer.${player}`, this.getPlayerDoc(ctx, player).onSnapshot(docSnap => {
            if (docSnap && docSnap.docExists()) {
                const gp = docSnap.data();
                if (gp) {
                    cb(gp);
                }
            }
        }, (err) => {
            errCb(ctx.chainError(err, 'listening to player ' + player));
        }));
        return unsub;
    }
    /**
     * Get the current game for the given player.
     */
    getCurrentGame(ctx, player) {
        ctx.log('Getting current game for player', player);
        const docRef = this.getPlayerDoc(ctx, player);
        return docRef.get().then(playerDoc => {
            var _a;
            const g = (_a = playerDoc === null || playerDoc === void 0 ? void 0 : playerDoc.data()) === null || _a === void 0 ? void 0 : _a.currentGame;
            if (!g) {
                return null;
            }
            return g;
        })
            .catch(err => {
            ctx.log('getCurrentGame ignoring:', err);
            return null;
        });
    }
    getGlobalPlayerCount(_ctx) {
        return this.players.getCount();
    }
}
exports.Lobby = Lobby;
Lobby.PLAYER_COLLECTION = 'globalPlayers';
//# sourceMappingURL=Lobby.js.map